import React, { PropsWithChildren, useState } from "react";

import {
  Link as MuiLink,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
  IconButton,
  MobileStepper,
} from "@mui/material";
import { ArrowBackIos } from "@mui/icons-material";

import { QuickAccessMenu } from "components/admin/QuickAccessMenu";
import { BlockStandard } from "components/admin/BlockStandard";
import { Link, LinkProps, useHistory } from "react-router-dom";

import { useUserContext } from "contexts/UserContext";
import { Header } from "components/libComponents/Header";
import { SideMenu } from "components/libComponents/SideMenu";

export const SolutionsFrame = ({
  title,
  h2Subtitle,
  explanationBody,
  imgSrc,
  imgAlt = "",
  buttonText,
  onButtonClick,
  isLoading,
  skipTo,
  isDisabled,
  hideReturn = false,
  currentStep,
  totalSteps,
  children,
}: SolutionsFrameProps) => {
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const { hasProsumer } = useUserContext();

  const handleMenuToggle = () => setIsMenuOpen((prevState) => !prevState);
  const handleClosing = () => setIsMenuOpen(false);

  return (
    <>
      <SideMenu
        open={isMenuOpen}
        onClose={handleClosing}
        isProsumer={hasProsumer}
      />
      <Header onDrawerToggle={handleMenuToggle} />
      <Grid
        container
        component="main"
        direction="column"
        sx={{
          overflowY: "scroll",
          gridArea: "main",
          py: 4,
          px: 3,
          flexWrap: "nowrap",
        }}
      >
        {!!title && !hideReturn && (
          <Grid
            item
            container
            direction="row"
            alignItems="center"
            sx={{ pb: 2 }}
          >
            <IconButton size="small" onClick={() => history.goBack()}>
              <ArrowBackIos
                sx={{ height: (theme) => theme.typography.caption.fontSize }}
              />
            </IconButton>
            {title && (
              <Typography variant="caption" component="h1">
                {title}
              </Typography>
            )}
          </Grid>
        )}

        {(!!currentStep || currentStep === 0) && !!totalSteps && (
          <Grid item container justifyContent="center">
            <MobileStepper
              variant="dots"
              position="static"
              steps={totalSteps}
              activeStep={currentStep}
              backButton={null}
              nextButton={null}
            />
          </Grid>
        )}

        {h2Subtitle && (
          <Typography sx={{ mt: 2 }} variant="h2">
            {h2Subtitle}
          </Typography>
        )}

        {explanationBody && (
          <Typography sx={{ mt: 2 }}>{explanationBody}</Typography>
        )}

        {imgSrc && (
          <BlockStandard hideDivider>
            <Card sx={{ boxShadow: "none" }}>
              <CardMedia
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <img style={{ height: "20vh" }} src={imgSrc} alt={imgAlt} />
              </CardMedia>
            </Card>
          </BlockStandard>
        )}
        <Grid item container sx={{ flex: 1 }}>
          {children}
        </Grid>

        {buttonText && onButtonClick && (
          <Button
            disabled={isLoading || isDisabled}
            sx={{ mt: 2 }}
            variant="contained"
            onClick={onButtonClick}
          >
            {isLoading ? <CircularProgress size={24} /> : buttonText}
          </Button>
        )}

        {skipTo && (
          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <MuiLink
              color="primary"
              component={Link}
              to={skipTo}
              underline="always"
              variant="body1"
            >
              Pular
            </MuiLink>
          </Grid>
        )}
      </Grid>

      <QuickAccessMenu />
    </>
  );
};

export type SolutionsFrameProps = PropsWithChildren<{
  title?: string;
  h2Subtitle?: string;
  explanationBody?: string;
  imgSrc?: string;
  imgAlt?: string;
  buttonText?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  skipTo?: LinkProps["to"];
  onButtonClick?: () => void;
  hideReturn?: boolean;
  currentStep?: number;
  totalSteps?: number;
}>;
